// VX-4834 follow-up: reload-durable escalation state (backoffLevel/lastBumpAt) for the ladder.
//
// VX-4834 made the failure COUNT survive a reload but left the escalation state resetting on every
// page load, and state.ts only ever persisted `generation`. Under the worklist navigation pattern this
// whole line of work targets, that means backoffLevel is 0 on every case-open, so:
//   - cooldownMs(0) is 0, making canBump unconditionally true — the 30s/1m/2m schedule never applies,
//     and a tab can bump, navigate, accumulate three more failures, and bump again immediately. Each
//     bump spawns a new SharedWorker generation and retires the old one, so an unpaced bump rate is
//     real WASM-heap churn, which is exactly what VX-4385's drain gate exists to bound.
//   - backoffLevel can never reach BACKOFF_CEILING (2), because proposeBump only ever takes a fresh
//     page from 0 to 1, so escapeHatchDegradedAtom is never set and the radiologist never sees the
//     "imaging delivery interrupted" indicator no matter how many bumps fire.
// Raised on #9512 (discussion_r3722846263). It was unreachable before, because the count never got to
// three across reloads; #9535 makes bumps actually fire, which makes it reachable.
//
// Why persisting this is safe now, when state.ts's original comment said it was not: that comment was
// reasoning about localStorage-style persistence, where "a fresh session" could inherit a previous
// session's escalation and show the degraded banner on a first stall. This store uses sessionStorage
// keyed by TAB_ID with an incident-length TTL, which is a materially different lifetime — a genuinely
// fresh session is a new tab, which shares no sessionStorage and inherits nothing. Only a reload
// inside the same tab, inside the same live incident, inherits. That is precisely the case the old
// reset-on-load was destroying.

// Keyed by TAB_ID, matching the other escape-hatch stores: escalation is this tab's own pacing state,
// and the cross-tab view is already handled by the atom's broadcastChannelSynchronizerEffect.
const storageKey = (tabId: string): string => `viewer.escapeHatch.escalation.${tabId}`;

// Escalation belongs to an INCIDENT, not to a browsing session, so it has to wind back down once the
// incident is over — which is the one thing the old reset-on-load did achieve, if crudely. Held equal
// to FAILED_RECREATE_EVIDENCE_TTL_MS on purpose: the escalation and the failure count describe the
// same incident, so they should expire together rather than one outliving the other and pacing bumps
// for an incident whose evidence is already gone.
export const ESCALATION_EVIDENCE_TTL_MS = 6 * 60_000;

export interface EscalationEvidence {
  backoffLevel: number;
  lastBumpAt: number;
}

interface StoredEscalation extends EscalationEvidence {
  // Epoch ms of the most recent write — the TTL window's origin. Distinct from lastBumpAt, which is
  // the cooldown's origin and must not be moved by an unrelated write.
  updatedAt: number;
}

const parse = (raw: string): StoredEscalation | null => {
  const value = JSON.parse(raw) as Partial<StoredEscalation> | null;
  if (
    value == null ||
    typeof value.backoffLevel !== 'number' ||
    typeof value.lastBumpAt !== 'number' ||
    typeof value.updatedAt !== 'number'
  ) {
    return null;
  }
  return value as StoredEscalation;
};

/**
 * This tab's escalation state for the current incident, or null when there is no trustworthy record
 * (nothing stored, stale, future-stamped, malformed, or storage unavailable). Null means "start
 * unescalated", which reproduces the previous behaviour — so every failure path degrades to what
 * shipped before rather than to something new.
 */
export function readEscalation({
  tabId,
  now,
}: {
  tabId: string;
  now: number;
}): EscalationEvidence | null {
  try {
    const raw = sessionStorage.getItem(storageKey(tabId));
    if (raw == null) return null;
    const stored = parse(raw);
    if (stored == null) return null;
    // A negative age means the clock moved backwards since the write (NTP correction, VM resume, a
    // user changing the clock). An unverifiable timestamp is not evidence: discard it rather than
    // hold a bump back for the length of the skew on top of the cooldown.
    const ageMs = now - stored.updatedAt;
    if (ageMs < 0 || ageMs > ESCALATION_EVIDENCE_TTL_MS) return null;
    return { backoffLevel: stored.backoffLevel, lastBumpAt: stored.lastBumpAt };
  } catch {
    return null;
  }
}

export function writeEscalation({
  tabId,
  backoffLevel,
  lastBumpAt,
  now,
}: {
  tabId: string;
  backoffLevel: number;
  lastBumpAt: number;
  now: number;
}): void {
  try {
    const stored: StoredEscalation = { backoffLevel, lastBumpAt, updatedAt: now };
    sessionStorage.setItem(storageKey(tabId), JSON.stringify(stored));
  } catch {
    // ignore write failures — the in-memory atom still paces bumps within this page
  }
}

export function clearEscalation({ tabId }: { tabId: string }): void {
  try {
    sessionStorage.removeItem(storageKey(tabId));
  } catch {
    // ignore
  }
}
